import { FieldDef, GroupDef, GuiDef, ValidatorDef, TabDefinition, CollapsableDef } from '@/objects/gui';
import { RelayTeamContestant } from '@/objects/generated';
import { markRaw, computed } from 'vue';
import {
    InputNullableInt,
    ValidatedInt,
    InputText,
    ValidatedText,
    InputDateTime,
    DateComp,
    InputSelect,
    DataSourceLookup,
} from '@/components';
import { type DateType } from '@/utilities/dateutils';
import { useCommonStore } from '@/stores';
import { storeToRefs } from 'pinia';
import type { SelectLine, SelectLineValueType } from '@/objects/SelectLine';

export class RelayTeamContestantGuiDef extends GuiDef<RelayTeamContestant> {
    constructor() {
        super('RelayTeamContestant');

        const Common = storeToRefs(useCommonStore());
        const GendersSource = computed(() => {
            return Common.GendersAsSelectLine.value;
        });

        const LegNumber = new FieldDef<RelayTeamContestant>();
        LegNumber.Name = 'LegNumber';
        LegNumber.FieldName = 'LegNumber';
        LegNumber.DefinedFor = 'Both';
        LegNumber.TranslationKey = 'relayteamcontestant.legnumber';
        LegNumber.Editable = true;
        LegNumber.Filterable = false;
        LegNumber.Sortable = true;
        LegNumber.CssClasses = '';
        LegNumber.value = (relayteamcontestant) => relayteamcontestant.LegNumber;
        LegNumber.setValue = (relayteamcontestant, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            relayteamcontestant.LegNumber = value;
        };
        LegNumber.DetailEditor = markRaw(InputNullableInt);
        LegNumber.DetailEditorProps = {};
        LegNumber.CellEditor = markRaw(InputNullableInt);
        LegNumber.CellEditorProps = {};
        LegNumber.CellRenderer = markRaw(ValidatedInt);
        LegNumber.CellRendererProps = {};
        LegNumber.Validator = null;
        LegNumber.SortDirection = undefined;
        LegNumber.cellClassRules = undefined;
        LegNumber.MinWidth = undefined;
        LegNumber.MaxWidth = undefined;
        LegNumber.Tooltip = null;
        this.Fields.push(LegNumber);

        const Firstname = new FieldDef<RelayTeamContestant>();
        Firstname.Name = 'Firstname';
        Firstname.FieldName = 'Firstname';
        Firstname.DefinedFor = 'Both';
        Firstname.TranslationKey = 'relayteamcontestant.firstname';
        Firstname.Editable = true;
        Firstname.Filterable = true;
        Firstname.Sortable = true;
        Firstname.CssClasses = '';
        Firstname.value = (relayteamcontestant) => relayteamcontestant.Firstname;
        Firstname.setValue = (relayteamcontestant, value) => (relayteamcontestant.Firstname = value);
        Firstname.DetailEditor = markRaw(InputText);
        Firstname.DetailEditorProps = {};
        Firstname.CellEditor = markRaw(InputText);
        Firstname.CellEditorProps = {};
        Firstname.CellRenderer = markRaw(ValidatedText);
        Firstname.CellRendererProps = {};
        Firstname.Validator = null;
        Firstname.SortDirection = undefined;
        Firstname.cellClassRules = undefined;
        Firstname.MinWidth = undefined;
        Firstname.MaxWidth = undefined;
        Firstname.Tooltip = null;
        this.Fields.push(Firstname);

        const Surname = new FieldDef<RelayTeamContestant>();
        Surname.Name = 'Surname';
        Surname.FieldName = 'Surname';
        Surname.DefinedFor = 'Both';
        Surname.TranslationKey = 'relayteamcontestant.surname';
        Surname.Editable = true;
        Surname.Filterable = true;
        Surname.Sortable = true;
        Surname.CssClasses = '';
        Surname.value = (relayteamcontestant) => relayteamcontestant.Surname;
        Surname.setValue = (relayteamcontestant, value) => (relayteamcontestant.Surname = value);
        Surname.DetailEditor = markRaw(InputText);
        Surname.DetailEditorProps = {};
        Surname.CellEditor = markRaw(InputText);
        Surname.CellEditorProps = {};
        Surname.CellRenderer = markRaw(ValidatedText);
        Surname.CellRendererProps = {};
        Surname.Validator = null;
        Surname.SortDirection = undefined;
        Surname.cellClassRules = undefined;
        Surname.MinWidth = undefined;
        Surname.MaxWidth = undefined;
        Surname.Tooltip = null;
        this.Fields.push(Surname);

        const Birthday = new FieldDef<RelayTeamContestant>();
        Birthday.Name = 'Birthday';
        Birthday.FieldName = 'Birthday';
        Birthday.DefinedFor = 'Both';
        Birthday.TranslationKey = 'relayteamcontestant.birthday';
        Birthday.Editable = true;
        Birthday.Filterable = true;
        Birthday.Sortable = true;
        Birthday.CssClasses = '';
        Birthday.value = (relayteamcontestant) => relayteamcontestant.Birthday;
        Birthday.setValue = (relayteamcontestant, value) => (relayteamcontestant.Birthday = value);
        Birthday.DetailEditor = markRaw(InputDateTime);
        Birthday.DetailEditorProps = { format: 'DATE' as DateType };
        Birthday.CellEditor = markRaw(InputDateTime);
        Birthday.CellEditorProps = { format: 'DATE' as DateType };
        Birthday.CellRenderer = markRaw(DateComp);
        Birthday.CellRendererProps = { format: 'DATE' as DateType };
        Birthday.Validator = null;
        Birthday.SortDirection = undefined;
        Birthday.cellClassRules = undefined;
        Birthday.MinWidth = 100;
        Birthday.MaxWidth = undefined;
        Birthday.Tooltip = null;
        this.Fields.push(Birthday);

        const Gender = new FieldDef<RelayTeamContestant>();
        Gender.Name = 'Gender';
        Gender.FieldName = 'Gender';
        Gender.DefinedFor = 'Both';
        Gender.TranslationKey = 'relayteamcontestant.gender';
        Gender.Editable = true;
        Gender.Filterable = true;
        Gender.Sortable = true;
        Gender.CssClasses = '';
        Gender.value = (relayteamcontestant) => relayteamcontestant.Gender;
        Gender.setValue = (relayteamcontestant, value) => (relayteamcontestant.Gender = value);
        Gender.DetailEditor = markRaw(InputSelect);
        Gender.DetailEditorProps = { datasource: { mapsource: GendersSource } };
        Gender.CellEditor = markRaw(InputSelect);
        Gender.CellEditorProps = { datasource: { mapsource: GendersSource } };
        // @ts-expect-error @ts-ignore
        Gender.CellRenderer = markRaw(DataSourceLookup);
        Gender.CellRendererProps = { datasource: { mapsource: GendersSource } };
        Gender.ExportValueFormatter = (obj: RelayTeamContestant) => {
            return GendersSource.value.get(obj.Gender)?.text;
        };
        Gender.Validator = null;
        Gender.SortDirection = undefined;
        Gender.cellClassRules = undefined;
        Gender.MinWidth = undefined;
        Gender.MaxWidth = undefined;
        Gender.Tooltip = null;
        this.Fields.push(Gender);

        const Email = new FieldDef<RelayTeamContestant>();
        Email.Name = 'Email';
        Email.FieldName = 'Email';
        Email.DefinedFor = 'Both';
        Email.TranslationKey = 'relayteamcontestant.email';
        Email.Editable = true;
        Email.Filterable = true;
        Email.Sortable = true;
        Email.CssClasses = '';
        Email.value = (relayteamcontestant) => relayteamcontestant.Email;
        Email.setValue = (relayteamcontestant, value) => (relayteamcontestant.Email = value);
        Email.DetailEditor = markRaw(InputText);
        Email.DetailEditorProps = {};
        Email.CellEditor = markRaw(InputText);
        Email.CellEditorProps = {};
        Email.CellRenderer = markRaw(ValidatedText);
        Email.CellRendererProps = {};
        Email.Validator = null;
        Email.SortDirection = undefined;
        Email.cellClassRules = undefined;
        Email.MinWidth = undefined;
        Email.MaxWidth = undefined;
        Email.Tooltip = null;
        this.Fields.push(Email);

        const Chip1 = new FieldDef<RelayTeamContestant>();
        Chip1.Name = 'Chip1';
        Chip1.FieldName = 'Chip1';
        Chip1.DefinedFor = 'Both';
        Chip1.TranslationKey = 'relayteamcontestant.chip1';
        Chip1.Editable = true;
        Chip1.Filterable = true;
        Chip1.Sortable = true;
        Chip1.CssClasses = '';
        Chip1.value = (relayteamcontestant) => relayteamcontestant.Chip1;
        Chip1.setValue = (relayteamcontestant, value) => (relayteamcontestant.Chip1 = value);
        Chip1.DetailEditor = markRaw(InputText);
        Chip1.DetailEditorProps = {};
        Chip1.CellEditor = markRaw(InputText);
        Chip1.CellEditorProps = {};
        Chip1.CellRenderer = markRaw(ValidatedText);
        Chip1.CellRendererProps = {};
        Chip1.Validator = null;
        Chip1.SortDirection = undefined;
        Chip1.cellClassRules = undefined;
        Chip1.MinWidth = undefined;
        Chip1.MaxWidth = undefined;
        Chip1.Tooltip = null;
        this.Fields.push(Chip1);

        const Chip2 = new FieldDef<RelayTeamContestant>();
        Chip2.Name = 'Chip2';
        Chip2.FieldName = 'Chip2';
        Chip2.DefinedFor = 'Both';
        Chip2.TranslationKey = 'relayteamcontestant.chip2';
        Chip2.Editable = true;
        Chip2.Filterable = true;
        Chip2.Sortable = true;
        Chip2.CssClasses = '';
        Chip2.value = (relayteamcontestant) => relayteamcontestant.Chip2;
        Chip2.setValue = (relayteamcontestant, value) => (relayteamcontestant.Chip2 = value);
        Chip2.DetailEditor = markRaw(InputText);
        Chip2.DetailEditorProps = {};
        Chip2.CellEditor = markRaw(InputText);
        Chip2.CellEditorProps = {};
        Chip2.CellRenderer = markRaw(ValidatedText);
        Chip2.CellRendererProps = {};
        Chip2.Validator = null;
        Chip2.SortDirection = undefined;
        Chip2.cellClassRules = undefined;
        Chip2.MinWidth = undefined;
        Chip2.MaxWidth = undefined;
        Chip2.Tooltip = null;
        this.Fields.push(Chip2);
    }

    public async LoadDependencies() {
        await useCommonStore().EnsureLoaded(['Genders']);
    }
}
